import React, { useState, useEffect, useRef } from "react"
import { Loader2, Check, AlertCircle } from "lucide-react"
import { Input } from "src/components/ui/input"
import { checkSubdomain } from "../api/qrCampaignsApi"

// Controlled subdomain input with a debounced availability check. Reports the
// resolved status up via onStatusChange({ available, host, reason }).
export function SubdomainField({ value, onChange, excludeId, onStatusChange }) {
  const [state, setState] = useState({ status: "idle" }) // idle | checking | ok | taken | invalid
  const timer = useRef(null)

  useEffect(() => {
    const v = (value || "").trim().toLowerCase()
    clearTimeout(timer.current)

    if (!v) {
      setState({ status: "idle" })
      onStatusChange?.({ available: false, host: null, reason: null })
      return
    }

    setState({ status: "checking" })
    timer.current = setTimeout(async () => {
      try {
        const res = await checkSubdomain(v, excludeId)
        if (res.available) {
          setState({ status: "ok", host: res.host })
        } else {
          setState({ status: res.reason?.includes("taken") ? "taken" : "invalid", reason: res.reason, host: res.host })
        }
        onStatusChange?.(res)
      } catch {
        setState({ status: "idle" })
      }
    }, 350)

    return () => clearTimeout(timer.current)
  }, [value, excludeId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          placeholder="onyx"
          className="max-w-[200px]"
          autoCapitalize="none"
          autoCorrect="off"
        />
        <span className="text-[13px] text-slate-400">.{appDomainSuffix(state.host)}</span>
        <StatusIcon status={state.status} />
      </div>
      <p className="mt-1 text-[12px] text-slate-400">
        Lowercase letters, numbers and hyphens. This becomes the public site host.
      </p>
      {state.status === "taken" && <Hint tone="err">That subdomain is already taken.</Hint>}
      {state.status === "invalid" && <Hint tone="err">{state.reason || "Invalid subdomain."}</Hint>}
      {state.status === "ok" && state.host && <Hint tone="ok">Available — {protocolHint()}{state.host}</Hint>}
    </div>
  )
}

function StatusIcon({ status }) {
  if (status === "checking") return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
  if (status === "ok") return <Check className="h-4 w-4 text-emerald-600" />
  if (status === "taken" || status === "invalid") return <AlertCircle className="h-4 w-4 text-red-500" />
  return null
}

function Hint({ tone, children }) {
  const cls = tone === "ok" ? "text-emerald-600" : "text-red-500"
  return <p className={`mt-1 text-[12px] ${cls}`}>{children}</p>
}

// Pull "clozdl.com" / "localhost:3000" out of the resolved host (which is
// "<sub>.<domain>"); fall back to a generic hint before the first check.
function appDomainSuffix(host) {
  if (!host) return "your-domain"
  const idx = host.indexOf(".")
  return idx >= 0 ? host.slice(idx + 1) : host
}

function protocolHint() {
  return typeof window !== "undefined" && window.location.protocol === "https:" ? "https://" : "http://"
}
